// Utility helpers to compute the completion status of a hotel's ads creative.
// Status values: "not_started" | "in_progress" | "done"
//
// NOTE: When the backend endpoint is ready, the per-hotel status should ideally
// come from the API. Until then we derive it on the client from the card data.

// Fields that must all be present for a single language card to be "complete".
const REQUIRED_CARD_FIELDS = [
  (card) => card?.ad_language_id,
  (card) => card?.primary_text?.assetContent,
  (card) => card?.web_site_url?.url,
  (card) => card?.headline?.assetContent,
  (card) => card?.call_to_action?.assetContent,
  (card) => card?.feed_link_description?.assetContent,
  (card) => card?.marketing_image?.url,
];

// Any of these being present means the user has started working on the card.
const ANY_CARD_FIELDS = [
  (card) => card?.ad_language_id,
  (card) => card?.primary_text?.assetContent,
  (card) => card?.web_site_url?.url,
  (card) => card?.headline?.assetContent,
  (card) => card?.call_to_action?.assetContent,
  (card) => card?.feed_link_description?.assetContent,
  (card) => card?.marketing_image?.url,
];

const isNonEmpty = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const isCardComplete = (card) =>
  REQUIRED_CARD_FIELDS.every((getField) => isNonEmpty(getField(card)));

const isCardTouched = (card) =>
  ANY_CARD_FIELDS.some((getField) => isNonEmpty(getField(card)));

/**
 * Computes the status of a hotel based on its language cards.
 * @param {Array} cards - the ads creative cards for a single hotel
 * @returns {"not_started" | "in_progress" | "done"}
 */
export const computeHotelStatus = (cards = []) => {
  const meaningfulCards = cards.filter((card) => card && card.ad_card_id != null);

  if (meaningfulCards.length === 0) return "not_started";

  const touchedCards = meaningfulCards.filter(isCardTouched);
  if (touchedCards.length === 0) return "not_started";

  const allComplete = touchedCards.every(isCardComplete);
  if (allComplete) return "done";

  return "in_progress";
};

export const STATUS_LABELS = {
  done: "DONE",
  in_progress: "IN PROGRESS",
  not_started: "NOT STARTED",
};
